import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { findByIdentityWithManager } from '../../services/identity';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { ExpenseService } from '../../services/expense';
import { StorageService } from '../../services/storage';
import { GraphQLExpenseService } from '../../services/graphqlExpense';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { notifyExpenseSubmitted } from '../../services/inAppNotification';
import { emailExpenseSubmitted } from '../../services/emailNotification';
import { getFinanceAdminEmails } from '../../services/userLoader';
import { Expense, ExpenseStatus, CreateExpenseInput, ExpenseCategory, PaymentMethod } from '../../types/expense';
import RightSidePanel from '../performance/RightSidePanel';
import ExpenseDetail from './ExpenseDetail';
import InlineLoader from '../common/InlineLoader';
import EmptyState from '../common/EmptyState';
import './MyExpensesPage.css';

type FilterKey = 'All' | ExpenseStatus;

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'All', label: 'All' },
  { key: ExpenseStatus.DRAFT, label: 'Draft' },
  { key: ExpenseStatus.SUBMITTED, label: 'Submitted' },
  { key: ExpenseStatus.MANAGER_APPROVED, label: 'Manager Approved' },
  { key: ExpenseStatus.FINANCE_APPROVED, label: 'Finance Approved' },
  { key: ExpenseStatus.REIMBURSED, label: 'Reimbursed' },
  { key: ExpenseStatus.MANAGER_REJECTED, label: 'Rejected' },
];

const EXPENSE_PAGE_SIZE = 25;

/** "All months", the month dropdown's resting state. */
const ALL_MONTHS = 'All';

/**
 * The `YYYY-MM` an expense belongs to, or '' when its date cannot be read.
 *
 * The leading ten characters are taken from the STRING when it looks like an ISO date, rather
 * than going through `new Date()`. `new Date('2026-03-01')` parses as UTC midnight, so anywhere
 * behind UTC it renders as 28 February — and an expense filed on the first of the month would
 * be filtered into the previous one. Reading the text avoids the question entirely.
 *
 * The Date fallback is for anything stored in another shape; it returns '' rather than throwing,
 * so one malformed row cannot empty the dropdown for every other month.
 */
export function monthKeyOf(date?: string): string {
  const text = String(date || '');
  if (/^\d{4}-\d{2}/.test(text)) return text.slice(0, 7);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-03" as "March 2026". Built from the parts, so it is not re-parsed into a timezone. */
export function monthLabelOf(key: string): string {
  const [year, month] = key.split('-');
  const index = Number(month) - 1;
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return names[index] ? `${names[index]} ${year}` : key;
}

function getExpenseStatusClass(status: ExpenseStatus): string {
  switch (status) {
    case ExpenseStatus.DRAFT: return 'exp-status-draft';
    case ExpenseStatus.SUBMITTED: return 'exp-status-submitted';
    case ExpenseStatus.MANAGER_APPROVED: return 'exp-status-manager-approved';
    case ExpenseStatus.FINANCE_APPROVED: return 'exp-status-finance-approved';
    case ExpenseStatus.REIMBURSED: return 'exp-status-reimbursed';
    case ExpenseStatus.MANAGER_REJECTED:
    case ExpenseStatus.FINANCE_REJECTED: return 'exp-status-rejected';
    default: return '';
  }
}

const MyExpensesPage: React.FC = () => {
  const renderStart = performance.now();
  console.log('[Perf][MyExpenses] render start');
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('All');
  const [activeMonth, setActiveMonth] = useState<string>(ALL_MONTHS);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [showNewExpensePanel, setShowNewExpensePanel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);

  const loadExpenses = useCallback(async (options: { append?: boolean; nextToken?: string } = {}) => {
    const startedAt = performance.now();
    console.log('[Perf][MyExpenses] API start ExpenseServiceWrapper.getMyExpenses', {
      userId: user?.id,
      userEmail: user?.email,
      append: Boolean(options.append),
      nextToken: options.nextToken,
    });
    try {
      if (options.append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const result = await ExpenseServiceWrapper.getMyExpenses(user?.id, user?.email, {
        limit: EXPENSE_PAGE_SIZE,
        nextToken: options.nextToken,
      });
      console.log('[Perf][MyExpenses] API complete ExpenseServiceWrapper.getMyExpenses', {
        durationMs: Math.round(performance.now() - startedAt),
        itemCount: result.items?.length || 0,
        total: result.total,
        nextToken: result.nextToken,
      });
      setNextToken(result.nextToken);
      setExpenses(prev => options.append ? [...prev, ...(result.items || [])] : (result.items || []));
    } catch (err) {
      console.error('Failed to load expenses:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user]);

  useEffect(() => { if (user) loadExpenses(); }, [user, loadExpenses]);

  /**
   * The months that actually have an expense, newest first.
   *
   * Built from the data rather than from a fixed twelve, because a list offering months nobody
   * filed anything in is a list of ways to empty the page. It grows as Load More brings older
   * pages in, which is also why the note below the filters exists.
   */
  const monthOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const exp of expenses) {
      const key = monthKeyOf(exp.date);
      if (key) keys.add(key);
    }
    return Array.from(keys).sort().reverse();
  }, [expenses]);

  /*
   * The month narrows the set that everything else works on — including the status counts.
   *
   * Applied first, deliberately. If the badges counted every loaded expense while the list showed
   * one month, the tab would say "Submitted 7" above four rows, and the number that disagrees
   * with what is on screen is the one people believe.
   */
  const monthExpenses = useMemo(() => {
    if (activeMonth === ALL_MONTHS) return expenses;
    return expenses.filter(e => monthKeyOf(e.date) === activeMonth);
  }, [expenses, activeMonth]);

  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = { All: monthExpenses.length };
    for (const exp of monthExpenses) {
      const key = exp.status === ExpenseStatus.FINANCE_REJECTED ? ExpenseStatus.MANAGER_REJECTED : exp.status;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [monthExpenses]);

  const filteredExpenses = useMemo(() => {
    if (activeFilter === 'All') return monthExpenses;
    if (activeFilter === ExpenseStatus.MANAGER_REJECTED) {
      return monthExpenses.filter(e => e.status === ExpenseStatus.MANAGER_REJECTED || e.status === ExpenseStatus.FINANCE_REJECTED);
    }
    return monthExpenses.filter(e => e.status === activeFilter);
  }, [monthExpenses, activeFilter]);

  /*
   * A month chosen from an earlier page can stop existing when the list reloads after a submit.
   * Falling back to All beats leaving the page filtered to a month with nothing in it and a
   * dropdown showing a value it no longer offers.
   */
  useEffect(() => {
    if (activeMonth !== ALL_MONTHS && !monthOptions.includes(activeMonth)) {
      setActiveMonth(ALL_MONTHS);
    }
  }, [monthOptions, activeMonth]);

  useEffect(() => {
    console.log('[Perf][MyExpenses] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      expenses: expenses.length,
      filteredExpenses: filteredExpenses.length,
      loading,
      activeFilter,
    });
  });

  const formatAmount = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const handleNewExpenseSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = e.currentTarget;
      const fd = new FormData(form);
      const input: CreateExpenseInput = {
        title: fd.get('title') as string,
        category: fd.get('category') as ExpenseCategory,
        amount: parseFloat(fd.get('amount') as string),
        date: fd.get('date') as string,
        description: fd.get('description') as string || '',
        paymentMethod: fd.get('paymentMethod') as PaymentMethod,
      };

      // Validate receipts
      if (receiptFiles.length === 0) {
        alert('Please attach at least one receipt before submitting.');
        setSubmitting(false);
        return;
      }

      const draft = await ExpenseServiceWrapper.createDraft(input, user?.email || user?.id, user?.name, user?.department);

      // Upload receipts
      for (const file of receiptFiles) {
        try {
          const receipt = await StorageService.uploadReceipt(draft.id, file);
          await GraphQLExpenseService.createReceipt({
            expenseId: draft.id, fileName: receipt.fileName, fileUrl: receipt.fileUrl,
            fileKey: receipt.fileKey, fileType: receipt.fileType, fileSize: receipt.fileSize,
          });
        } catch (err) { console.error('Receipt upload failed:', file.name, err); }
      }

      // Get manager email
      let managerEmail = user?.managerEmail;
      if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
        try {
          const response = await CognitoAdminService.listUsers();
          const cognitoUser = findByIdentityWithManager(response.users, user?.email);
          if (cognitoUser?.managerEmail) managerEmail = cognitoUser.managerEmail;
        } catch {}
      }

      const submitted = await ExpenseServiceWrapper.submitForApproval(draft.id, { role: user?.role, managerEmail });
      const employeeName = user?.name || 'Employee';

      if (submitted.status === ExpenseStatus.SUBMITTED && managerEmail) {
        await notifyExpenseSubmitted(managerEmail, employeeName, input.title, input.amount, draft.id);
        await emailExpenseSubmitted(managerEmail, employeeName, input.title, input.amount, draft.id);
      } else if (submitted.status === ExpenseStatus.MANAGER_APPROVED) {
        const financeEmails = await getFinanceAdminEmails();
        for (const email of financeEmails) {
          await notifyExpenseSubmitted(email, employeeName, input.title, input.amount, draft.id);
          await emailExpenseSubmitted(email, employeeName, input.title, input.amount, draft.id);
        }
      }

      setShowNewExpensePanel(false);
      setReceiptFiles([]);
      await loadExpenses();
    } catch (err) {
      console.error('Failed to submit expense:', err);
      alert('Failed to submit expense. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      for (const f of newFiles) {
        const v = StorageService.validateFile(f);
        if (!v.isValid) { alert(`${f.name}: ${v.error}`); return; }
      }
      setReceiptFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => setReceiptFiles(prev => prev.filter((_, i) => i !== index));

  return (
    <section className="my-expenses-page">
      {/* Header */}
      <div className="my-expenses-page__header">
        <h2 className="my-expenses-page__title">My Expenses</h2>

        {/*
          * The month sits on the header line, beside New Expense.
          *
          * A select rather than another row of pills: the months only grow, and a scrolling strip
          * of them would push the status tabs — the thing people came to press — off the first
          * screenful within a year of using the page.
          *
          * It is rendered only once there is something to filter. A permanently disabled dropdown
          * over an empty page is a control that can only disappoint, and on the header line it
          * would also leave a gap beside the button.
          */}
        <div className="my-expenses-page__header-actions">
          {monthOptions.length > 0 && (
            <div className="my-expenses-page__month">
              <label htmlFor="expense-month">Month</label>
              <select
                id="expense-month"
                value={activeMonth}
                onChange={e => setActiveMonth(e.target.value)}
              >
                <option value={ALL_MONTHS}>All months</option>
                {monthOptions.map(key => (
                  <option key={key} value={key}>{monthLabelOf(key)}</option>
                ))}
              </select>
            </div>
          )}
          <button className="my-expenses-page__new-btn" onClick={() => { setShowNewExpensePanel(true); setReceiptFiles([]); }}>
            + New Expense
          </button>
        </div>
      </div>

      {/*
        * Said plainly, because the filter can only see what has been fetched.
        *
        * The page loads 25 at a time and nothing here queries by date, so a month whose expenses
        * are all on a later page is a month the dropdown does not offer — and silently, if this
        * line were not here.
        *
        * On its own line under the header rather than beside the select: it is a sentence, and a
        * sentence on the same line as the page's primary button either wraps the header onto two
        * rows or squeezes the button.
        */}
      {!loading && nextToken && monthOptions.length > 0 && (
        <p className="my-expenses-page__month-note">
          Older expenses aren’t loaded yet — use Load More to include them.
        </p>
      )}

      {/* Filter tabs */}
      <div className="my-expenses-page__filters" role="toolbar" aria-label="Filter by status">
        {FILTER_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            className={`my-expenses-page__filter-btn ${activeFilter === key ? 'my-expenses-page__filter-btn--active' : ''}`}
            onClick={() => setActiveFilter(key)}
            aria-pressed={activeFilter === key}
          >
            {label}
            <span className="my-expenses-page__filter-badge">{countByStatus[key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Expense cards */}
      <div className="my-expenses-page__items">
        {loading ? (
          <InlineLoader message="Loading expenses" />
        ) : filteredExpenses.length === 0 ? (
          /*
           * Which nothing this is. "Create your first expense" is wrong advice for somebody who
           * has forty expenses and picked a quiet month, and it is wrong again when the answer
           * is simply on a page that has not been fetched — so when there is more to load, the
           * action is to load it.
           */
          activeMonth !== ALL_MONTHS ? (
            <EmptyState
              icon="inbox"
              title={`Nothing in ${monthLabelOf(activeMonth)}`}
              subtitle={
                nextToken
                  ? 'No expenses this month among the ones loaded so far. Load more to search older expenses.'
                  : 'No expenses were filed in this month.'
              }
              actionLabel={nextToken ? 'Load more' : 'Show all months'}
              onAction={() =>
                nextToken ? loadExpenses({ append: true, nextToken }) : setActiveMonth(ALL_MONTHS)
              }
            />
          ) : (
            <EmptyState
              icon="inbox"
              title="No expenses found"
              subtitle="Create your first expense to get started with tracking."
              actionLabel="+ New Expense"
              onAction={() => { setShowNewExpensePanel(true); setReceiptFiles([]); }}
            />
          )
        ) : (
          filteredExpenses.map(exp => (
            <div key={exp.id} className="my-expenses-card" onClick={() => setSelectedExpense(exp)} role="button" tabIndex={0}>
              <div className="my-expenses-card__header">
                <h3 className="my-expenses-card__title">{exp.title}</h3>
                <span className={`my-expenses-card__status ${getExpenseStatusClass(exp.status)}`}>{exp.status}</span>
              </div>
              <div className="my-expenses-card__meta">
                <span className="my-expenses-card__amount">{formatAmount(exp.amount)}</span>
                <span className="my-expenses-card__category">{exp.category}</span>
                <span className="my-expenses-card__date">{formatDate(exp.date)}</span>
              </div>
            </div>
          ))
        )}
        {!loading && nextToken && (
          <button
            type="button"
            className="my-expenses-page__new-btn"
            onClick={() => loadExpenses({ append: true, nextToken })}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        )}
      </div>

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="my-expenses-detail-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="my-expenses-detail-wrapper" onClick={e => e.stopPropagation()}>
            <ExpenseDetail expense={selectedExpense} onClose={() => setSelectedExpense(null)} onUpdate={() => { setSelectedExpense(null); loadExpenses(); }} />
          </div>
        </div>
      )}

      {/* New Expense Side Panel */}
      <RightSidePanel isOpen={showNewExpensePanel} title="New Expense" onClose={() => setShowNewExpensePanel(false)} wide>
        <form className="my-expenses-form" onSubmit={handleNewExpenseSubmit}>
          <div className="my-expenses-form__field">
            <label>Title *</label>
            <input name="title" type="text" required placeholder="e.g. Client dinner, Travel to Mumbai" />
          </div>
          <div className="my-expenses-form__row">
            <div className="my-expenses-form__field">
              <label>Amount (₹) *</label>
              <input name="amount" type="number" step="0.01" min="0.01" required placeholder="0.00" />
            </div>
            <div className="my-expenses-form__field">
              <label>Date *</label>
              <input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
          </div>
          <div className="my-expenses-form__row">
            <div className="my-expenses-form__field">
              <label>Category *</label>
              <select name="category" required defaultValue={ExpenseCategory.OTHER}>
                {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="my-expenses-form__field">
              <label>Payment Method *</label>
              <select name="paymentMethod" required defaultValue={PaymentMethod.PERSONAL_CARD}>
                {Object.values(PaymentMethod).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="my-expenses-form__field">
            <label>Description</label>
            <textarea name="description" rows={3} placeholder="Optional details about this expense" />
          </div>
          <div className="my-expenses-form__field">
            <label>Receipts * <span className="my-expenses-form__hint">(PDF, JPG, PNG — max 10MB)</span></label>
            <div className="my-expenses-form__receipts">
              {receiptFiles.map((f, i) => (
                <div key={i} className="my-expenses-form__receipt-item">
                  <span>{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)}>✕</button>
                </div>
              ))}
              <label className="my-expenses-form__upload-btn">
                📎 Add Receipt
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
            </div>
          </div>
          <div className="my-expenses-form__actions">
            <button type="button" className="my-expenses-form__cancel" onClick={() => setShowNewExpensePanel(false)}>Cancel</button>
            <button type="submit" className="my-expenses-form__submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Expense'}
            </button>
          </div>
        </form>
      </RightSidePanel>
    </section>
  );
};

export default MyExpensesPage;
